import React, { useState , useEffect} from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { baseUrl } from "../../../../Utils/Baseurl/Baseurl";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Modal from "react-modal";
interface Supplier {
    name: string;
}
interface ChemicalContent {
    name: string;
    quantity: string;
}
interface IndentFormData {
    tradeName: string;
    chemicalContents: string;
    suppliers: Supplier[];
    manufacturer: string;
    therapeuticClass: string;
    representativeName: string;
    contactNo: string;
}

const IndentForm: React.FC = () => {
    const { tokenid } = useParams<{ tokenid: string }>();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const navigate = useNavigate();
    const { register, handleSubmit, reset, formState: { errors } ,setValue} = useForm<IndentFormData>({
        defaultValues: {
          tradeName: "",
          manufacturer: "",
          therapeuticClass: "",
          representativeName: "",
          contactNo: "",
        }
      });
      
    // const { register, handleSubmit, reset, formState: { errors } } = useForm<IndentFormData>();
    const [suppliers, setSuppliers] = useState<Supplier[]>([{ name: "" }]);
    const [chemicalContents, setChemicalContents] = useState<ChemicalContent[]>([{ name: "", quantity: "" }]);
    const [fetchedIndent, setFetchedIndent] = useState<IndentFormData | null>(null);

    const updateChemicalContent = (index: number, field: keyof ChemicalContent, value: string) => {
        const updatedContents = [...chemicalContents];
        updatedContents[index][field] = value;
        setChemicalContents(updatedContents);
    };

    const addChemicalContent = () => {
        setChemicalContents([...chemicalContents, { name: "", quantity: "" }]);
    };

    const removeChemicalContent = (index: number) => {
        const updatedContents = chemicalContents.filter((_, i) => i !== index);
        setChemicalContents(updatedContents);
    };


    const handleSupplierChange = (index: number, value: string) => {
        const updatedSuppliers = [...suppliers];
        updatedSuppliers[index].name = value;
        setSuppliers(updatedSuppliers);
    };

    const addSupplier = () => {
        setSuppliers([...suppliers, { name: "" }]);
    };

    const removeSupplier = (index: number) => {
        const updatedSuppliers = suppliers.filter((_, i) => i !== index);
        setSuppliers(updatedSuppliers);
    };

   
    useEffect(() => {
        const fetchIndentData = async () => {
            try {
                const response = await axios.get(`${baseUrl}indentform/getIndentForm/${tokenid}`);
                const data = response.data;
    
                if (data) {
                    reset({
                        tradeName: data.tradeName || "",
                        manufacturer: data.manufacturer || "",
                        therapeuticClass: data.therapeuticClass || "",
                        representativeName: data.representativeName || "",
                        contactNo: data.contactNo || "",
                    });
    
                    setSuppliers(data.supplier.length ? data.supplier : [{ name: "" }]);
                    setChemicalContents(data.chemicalContents.length ? data.chemicalContents : [{ name: "", quantity: "" }]);
    
                    // ✅ Store fetched data
                    setFetchedIndent(data);
                }
            } catch (error) {
                // console.error("Error fetching indent form:", error);
                // toast.error("Error fetching indent form data", { position: "top-right" });
            }
        };
    
        if (tokenid) {
            fetchIndentData();
        }
    }, [tokenid, reset]);
  
    
    
    const onSubmit = async (data: IndentFormData) => {
        try {
            let hasErrors = false;
    
            // ✅ Validate required fields
            if (!data.therapeuticClass.trim()) {
                toast.error("Therapeutic Class is required.", { position: "top-right" });
                hasErrors = true;
            }
            if (!data.representativeName.trim()) {
                toast.error("Representative Name is required.", { position: "top-right" });
                hasErrors = true;
            }
    
            const contactNo = data.contactNo.trim();
            if (!contactNo) {
                toast.error("Contact Number is required.", { position: "top-right" });
                hasErrors = true;
            } else if (contactNo.length < 10) {
                toast.error("Contact Number must be at least 10 digits.", { position: "top-right" });
                hasErrors = true;
            } else if (contactNo.length > 15) {
                toast.error("Contact Number cannot exceed 15 digits.", { position: "top-right" });
                hasErrors = true;
            }
    
            if (!chemicalContents.length || chemicalContents.some(c => !c.name.trim() || !c.quantity.trim())) {
                toast.error("Please enter at least one valid Chemical Content (Name & Quantity).", { position: "top-right" });
                hasErrors = true;
            }
    
            if (!suppliers.length || suppliers.some(s => !s.name.trim())) {
                toast.error("Please enter at least one supplier name.", { position: "top-right" });
                hasErrors = true;
            }
    
            if (hasErrors) return;
    
            // ✅ Determine whether to create or update
            const url = fetchedIndent
                ? `${baseUrl}indentform/updateIndentForm/${tokenid}` // Update existing record
                : `${baseUrl}indentform/saveindentform`; // Create new record
            const method = fetchedIndent ? "put" : "post";
    
            const response = await axios({
                method,
                url,
                data: {
                    ...data,
                    tokenid,
                    suppliers,
                    chemicalContents
                }
            });
    
            toast.success(response.data.message, { position: "top-right" });
    
            // ✅ Open the modal and do NOT navigate immediately
            setModalIsOpen(true);
    
        } catch (error: any) {
            if (error.response?.data?.message === "Contact Number is already registered.") {
                toast.error("This Contact Number is already registered.", { position: "top-right" });
            } else {
                toast.error(error.response?.data?.message || "Error submitting form", { position: "top-right" });
            }
        }
    };
    

    const handleCloseModal = () => {
        setModalIsOpen(false);
    
        setTimeout(() => {
            reset();
            setSuppliers([{ name: "" }]);
            setChemicalContents([{ name: "", quantity: "" }]);
            setFetchedIndent(null);
            navigate("/admiro/token");  // ✅ Navigate AFTER user closes modal
        }, 1000);
    };
    

    const downloadPdf = async () => {
        setIsDownloading(true); // Show loader

        try {
            const response = await fetch(`${baseUrl}tokenpdf/generate-indentpdf/${tokenid}`);

            if (!response.ok) {
                throw new Error("Failed to download PDF");
            }

            // Convert to Blob and trigger download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "indent.pdf"; // Set filename
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            // Hide loader and navigate after download completes
            setIsDownloading(false);
            handleCloseModal();
        } catch (error) {
            console.error("Download failed:", error);
            setIsDownloading(false);
        }
    };

    const customStyles: ReactModal.Styles = {
        content: {
            width: "450px",
            height: "250px",
            margin: "auto",
            textAlign: "center",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #ffffff, #f1f1f1)",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
            padding: "20px",
            display: "flex",
            flexDirection: "column", // ✅ Fix: No longer a string, now a valid FlexDirection type
            alignItems: "center",
            justifyContent: "center",
        },
    };

    const buttonStyle = {
        padding: "10px 16px",
        margin: "10px",
        borderRadius: "8px",
        fontSize: "16px",
        fontWeight: "bold",
        cursor: "pointer",
        border: "none",
        transition: "0.3s",
    };

    const downloadButton = {
        ...buttonStyle,
        background: "linear-gradient(135deg, #007bff, #0056b3)",
        color: "#fff",
    };

    const closeButton = {
        ...buttonStyle,
        background: "linear-gradient(135deg, #ff4d4d, #b30000)",
        color: "#fff",
    };

    useEffect(() => {
        (async () => {
            try {
                const response = await axios.get(`${baseUrl}medicinedetails/getByToken/${tokenid}`);
                console.log("Response:", response);
                const medicineData = response.data.data;
    
                if (Array.isArray(medicineData) && medicineData.length > 0) {
                    const medicine = medicineData[0];
                    console.log("First medicine entry:", medicine);
    
                    setValue("tradeName", medicine.medicinename || "");
    
                    if (medicine.contents && medicine.contents.length) {
                        setChemicalContents(medicine.contents);
                    } else {
                        setChemicalContents([{ name: "", quantity: "" }]);
                    }
                } else {
                    console.log("No medicine found.");
                }
            } catch (error) {
                console.error("Failed to fetch medicine:", error);
            }
        })();
    }, [tokenid, setValue]);
    
    
    
    
    return (
        <div style={styles.formContainer}>
            <h2 style={styles.heading}>Enter the Indent Details for New Drug/Surgicals</h2>
            <form onSubmit={handleSubmit(onSubmit)} noValidate style={styles.form}>



                {[{ name: "tradeName", label: "Trade Name" },
                { name: "manufacturer", label: "Manufacturer" },
                { name: "therapeuticClass", label: "Therapeutic Class" },
                { name: "representativeName", label: "Representative Name" },
                { name: "contactNo", label: "Contact No", type: "tel" },
                ].map(({ name, label, type }) => (
                    <div key={name} style={styles.formGroup}>
                        <label style={styles.label}>{label}</label>
                        <input type={type || "text"} {...register(name as keyof IndentFormData, { required: `${label} is required` })} style={styles.input} />
                        {errors[name as keyof IndentFormData] && <span style={styles.error}>{errors[name as keyof IndentFormData]?.message}</span>}
                    </div>
                ))}

              
                  {/* Chemical Contents */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Chemical Contents</label>
                    {chemicalContents.map((content, index) => (
                        <div key={index} style={{ display: "flex", gap: "10px", alignItems: "center",  marginBottom: "10px" }}>
                            <input
                                type="text"
                                placeholder="Content Name"
                                value={content.name}
                                onChange={(e) => updateChemicalContent(index, "name", e.target.value)}
                                style={styles.input }
                            />
                            <input
                                type="text"
                                placeholder="Quantity (gms)"
                                value={content.quantity}
                                onChange={(e) => updateChemicalContent(index, "quantity", e.target.value)}
                                style={styles.input}
                            />
                            {/* <button type="button" onClick={addChemicalContent} style={styles.addBtn}>➕ </button>
                            <button type="button" onClick={() => removeChemicalContent(index)} style={styles.deleteBtn}>❌</button>
                         */}
                        </div>
                    ))}
                </div>

                {/* Supplier Fields */}
                <div style={styles.formGroup}>
                    <label style={styles.label}>Suppliers</label>
                    {suppliers.map((supplier, index) => (
                        <div key={index} style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                            <input
                                type="text"
                                value={supplier.name}
                                onChange={(e) => handleSupplierChange(index, e.target.value)}
                                style={styles.input}
                                required
                            />
                            <button type="button" onClick={addSupplier} style={styles.addBtn}>➕  </button>
                            <button type="button" onClick={() => removeSupplier(index)} style={styles.deleteBtn}>❌</button>
                        </div>
                    ))}
                </div>

                <button type="submit" style={styles.submitBtn}>Submit</button>
                <Modal
                    isOpen={modalIsOpen}
                    onRequestClose={handleCloseModal}
                    style={customStyles}
                >
                    <h2 style={{ color: "#333", fontSize: "20px" }}>✅ Indent Submitted Successfully</h2>
                    <p style={{ color: "#555", fontSize: "16px" }}>Now you can download your indent.</p>

                    {isDownloading && <p style={{ color: "blue" }}>Downloading... ⏳</p>} {/* Loader */}

                    <div>
                        <button
                            style={downloadButton}
                            disabled={isDownloading} // Disable while downloading
                            onClick={downloadPdf}
                        >
                            📄 {isDownloading ? "Downloading..." : "Download PDF"}
                        </button>

                        <button
                            style={closeButton}
                            disabled={isDownloading} // Disable close during download
                            onClick={handleCloseModal}
                        >
                            ❌ Close
                        </button>
                    </div>
                </Modal>
            </form>
        </div>
    );
};

// Styles
const styles: { [key: string]: React.CSSProperties } = {
    formContainer: {
        width: "50%",
        margin: "20px auto",
        background: "#fff",
        padding: "20px",
        borderRadius: "10px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    },
    heading: {
        textAlign: "center",
        marginBottom: "20px",
        fontSize: "22px",
        color: "#2c3e50",
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: "15px",
    },
    formGroup: {
        display: "flex",
        flexDirection: "column",
    },
    label: {
        fontWeight: 600,
        marginBottom: "5px",
    },
    input: {
        padding: "10px",
        border: "1px solid #ccc",
        borderRadius: "5px",
        fontSize: "16px",
    },
    error: {
        color: "red",
        fontSize: "14px",
        marginTop: "5px",
    },
    addBtn: {
        backgroundColor: "#3498db",
        color: "white",
        padding: "5px 10px",
        border: "none",
        borderRadius: "5px",
        fontSize: "14px",
        cursor: "pointer",
        display: "inline-block",  // Prevent full-width stretch
        width: "40px",  // Match delete button width
        textAlign: "center",
    },
    deleteBtn: {
        backgroundColor: "#e74c3c",
        color: "white",
        padding: "5px 10px",
        border: "none",
        borderRadius: "5px",
        fontSize: "14px",
        cursor: "pointer",
    },
    submitBtn: {
        backgroundColor: "#2ecc71",
        color: "white",
        padding: "12px",
        border: "none",
        borderRadius: "5px",
        fontSize: "18px",
        cursor: "pointer",
        transition: "background 0.3s",
    },
};

export default IndentForm;

